import { BankAccount, Invoice, InvoiceArticle } from '@thro-types/billing';

import { TFunction } from 'i18next';
import { pick } from 'lodash';
import * as yup from 'yup';
import { ZodError } from 'zod';

import { translations } from 'locales/i18n';

import { customerValidationSchema } from '../../Customer/CustomerDrawer/validation';
import { isForeignCustomer } from '../../utils';

export const invoiceValidationSchema = (t: TFunction) => yup.object().shape({});

const censoredString = '*******';

export const invoiceReadyValidationSchema = (t: TFunction) =>
  yup.object().shape({
    customerVersionId: yup
      .string()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required())),
    expertContactVersionId: yup
      .string()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required())),
    bankAccount: yup
      .string()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required())),
    paymentTarget: yup
      .string()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required())),
    invoiceDate: yup
      .date()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required())),
    revenueTypeVersionId: yup
      .string()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required())),
    chapter: yup
      .string()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required()))
      .matches(/^0\d{4}$/, t(translations.pages.billing.validation.onlyNumbersLeadingZero())),
    title: yup
      .string()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required())),
    invoiceArticles: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required(t(translations.validation.required())),
          description: yup.string().required(t(translations.validation.required())),
          amount: yup.number().required(t(translations.validation.required())),
          price: yup.number().required(t(translations.validation.required()))
        })
      )
      .min(1, t(translations.validation.minCreateOne()))
      .test(
        'salesTaxKey 41 or 21',
        t(translations.pages.billing.validation.ustidKey()),
        function () {
          const { invoiceArticles, customer } = this.parent;
          const customerUSTID = customer?.ustId;
          const requiresUSTID = invoiceArticles.some(article =>
            ['41', '21'].includes(article?.salestax?.key)
          );
          if (!requiresUSTID) return true;
          if (requiresUSTID && !customerUSTID) return false;

          return true;
        }
      )
      // Tax Key 45 only allows foreign customers AND must NOT have a valid VAT ID
      // (only DE000000000 for companies as dummy Vat ID is allowed)
      .test('salesTaxKey 45', t(translations.pages.billing.validation.ustidKey45()), function () {
        const { invoiceArticles, customer } = this.parent;
        const includeSalesTaxKey45 = invoiceArticles.some(article =>
          ['45'].includes(article?.salestax?.key)
        );
        if (!includeSalesTaxKey45) return true;
        const isForeign = isForeignCustomer(customer);
        const hasNoVatId = !customer?.ustId || customer?.ustId === 'DE000000000';
        if (!hasNoVatId || !isForeign) return false;
        return true;
      })
      .test(
        'allSalesTaxesSame',
        t(translations.pages.billing.validation.taxesAllSame()),
        function () {
          if (this.parent.state !== 'DRAFT')
            return this.parent.invoiceArticles.every(
              (article: InvoiceArticle, _, arr: InvoiceArticle[]) =>
                article.salestaxVersionId === arr[0].salestaxVersionId
            );
          return true;
        }
      ),
    //Remove that test because Reverse charge is only when 01
    // .test('reverseCharge', t(translations.pages.billing.validation.reverseCharge()), function () {
    //   return !(isForeignCustomer(this.parent.customer) && hasArticleWithTaxNotZero(this.parent));
    // }),
    reminderKey: yup
      .string()
      .nullable()
      .when('bankAccount', {
        is: BankAccount.STOK,
        then: yup
          .string()
          .oneOf(['13', '14'], t(translations.pages.billing.validation.reminderKey()))
          .typeError(t(translations.validation.required()))
      }),
    insArt: yup.string().required(t(translations.validation.required())),
    costBearer: yup
      .string()
      .required(t(translations.validation.required()))
      .typeError(t(translations.validation.required())),
    // .test('costBearer', t(translations.pages.billing.validation.costBearer()), function () {
    //   return this.parent.costBearer || this.parent.costCentre;
    // }),
    departmentVersionId: yup.string().nullable(),
    customer: yup
      .object()
      .shape({
        zip: yup
          .string()
          .required(t(translations.validation.required()))
          .typeError(t(translations.validation.required()))
          .test(
            'censoredTest',
            t(translations.validation.required()),
            value => value !== censoredString
          ),
        city: yup
          .string()
          .required(t(translations.validation.required()))
          .typeError(t(translations.validation.required()))
          .test(
            'censoredTest',
            t(translations.validation.required()),
            value => value !== censoredString
          ),
        address: yup
          .string()
          .required(t(translations.validation.required()))
          .typeError(t(translations.validation.required()))
          .test(
            'censoredTest',
            t(translations.validation.required()),
            value => value !== censoredString
          )
      })
      .test('CustomerTest', async function () {
        const result = await customerValidationSchema({ shouldVatCheckLocal: true }).safeParseAsync(
          this.parent.customer
        );
        if (result.error instanceof ZodError && !result.success) {
          const errorMap: Record<string, string> = {};
          for (const issue of result.error.issues) {
            const firstPath = issue.path[0]; // use only first path
            if (firstPath === 'saveCustomer') continue; // saveCustomer is only for customer Drawer

            if (typeof firstPath === 'string' || typeof firstPath === 'number') {
              errorMap[String(firstPath)] = issue.message;
            }
          }
          if (Object.keys(errorMap).length === 0) {
            return true;
          }
          return this.createError({
            path: 'customer',
            message: { ...errorMap }
          });
        }

        return true;
      }),
    additionalRecipients: yup.array().of(yup.string().email(t(translations.validation.email())))
  });

export const invoiceInitialValues = (initialInvoice: Invoice | undefined) => {
  return initialInvoice
    ? {
        ...pick(initialInvoice, [
          'id',
          'customer',
          'customerVersionId',
          'paymentTarget',
          'bankAccount',
          'invoiceArticles',
          'invoiceDate',
          'salestax',
          'salestaxVersionId',
          'expertContact',
          'expertContactVersionId',
          'servicePeriodStart',
          'servicePeriodEnd',
          'yourReference',
          'revenueTypeVersionId',
          'revenueType',
          'chapter',
          'reminderKey',
          'costBearer',
          'costCentre',
          'insArt',
          'title',
          'department',
          'departmentVersionId',
          'additionalRecipients'
        ])
      }
    : {
        customer: '',
        paymentTarget: '',
        bankAccount: '',
        salestax: '',
        expertContact: {},
        yourReference: '',
        cancellationReason: '',
        servicePeriodStart: '',
        servicePeriodEnd: '',
        costCentre: '',
        costBearer: '',
        invoiceDate: '',
        department: '',
        insArt: '',
        additionalRecipients: []
      };
};
